import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Container,
  PageTitle,
  SectionTitle,
  SubSectionTitle,
  CardBase,
  ColumnBase,
  HStack,
  VStack,
  Button,
  SearchField,
  SelectBox,
  CheckBox,
  Text,
  Note,
  StatusIcon,
  Breadcrumbs,
  MessageBlock,
  FloatingMessageBlock,
  TabBar,
  ProgressBar,
  InlineSpinner,
} from '@freee_jp/vibes';
import { MdAssignment, MdWarning, MdEventNote, MdLoop, MdCheckCircle } from 'react-icons/md';
import { batch1Summary } from '../data/batch1';
import { JournalEntry, VerificationStatus, JournalDetail } from '../types/journal';
import { PdfViewer, PdfViewerModal } from '../components/PdfViewer';
import { CorrectionModal, CorrectionAction } from '../components/CorrectionModal';
import { verificationStatusToUnified } from '../lib/statusVocab';
import { DealProposalsPanel } from '../components/DealProposalsPanel';
import { ReceiptTrackingPanel } from '../components/ReceiptTrackingPanel';
import { PartnerCadencePanel } from '../components/PartnerCadencePanel';
import { ActivityPanel } from '../components/ActivityPanel';
import { InvoiceCoverageTab } from '../components/InvoiceCoverageTab';
import { BankAutoDebitInvoicePage } from './BankAutoDebitInvoicePage';
import {
  registerApprovedBatch,
  BatchEntry,
  approveDealProposal,
  rejectDealProposal,
  checkRegisteredRefs,
  fetchDealProposals,
  fetchDealProposalsByTargetMonth,
  fetchDeferredInvoices,
  resolveDeferredInvoice,
  requeueDeferredInvoice,
  registerProposalViaWorker,
  createBatch,
  fetchBatchDetail,
  backfillReceipts,
  fetchAccountItemsMap,
  DealProposal,
  DeferredInvoiceItem,
  notifyAuditBlocked,
} from '../api/client';
import { resolveAccountLabel } from '../utils/accountDisplay';
import type { PdfViewerFile } from '../components/PdfViewer';

// 2026-05-19 Option A: register-via-worker 用 session 確保 (= InvoiceCoverageTab と同等)
const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
async function ensureSessionLocal(): Promise<string | null> {
  try {
    const existing = localStorage.getItem('claude_session_id');
    if (existing) {
      const hb = await fetch(`${API_BASE}/api/claude/session/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: existing }),
      }).catch(() => null);
      if (hb && hb.ok) return existing;
    }
    const r = await fetch(`${API_BASE}/api/claude/session/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actor: 'browser-ui', scope: 'journal_verification_register', lease_minutes: 120 }),
    });
    if (!r.ok) {
      // 2026-07-15 blockfix: audit blocking 409 は共通ダイアログ (自動修復ボタン付き) へ通知
      if (r.status === 409) notifyAuditBlocked(await r.json().catch(() => null));
      return null;
    }
    const data = await r.json();
    if (data.session_id) {
      localStorage.setItem('claude_session_id', data.session_id);
      return data.session_id;
    }
    return null;
  } catch {
    return null;
  }
}

// Canonical dedupe key per entry. Uses explicit refNumber when set (for split invoices like 21474-A/B),
// otherwise derives from rowid.
function entryRefNumber(e: JournalEntry): string {
  return e.refNumber || `arica:${e.rowid}`;
}

// Build a list of PdfViewerFile entries for all receipts attached to a proposal.
// Prefers attached_rowids (ARICA proxy URLs — publicly accessible) over freee
// receipt download URLs (which require auth and can't be fetched from browser).
function receiptsForProposal(p: DealProposal | null | undefined): PdfViewerFile[] {
  if (!p) return [];
  const deal: any = p.proposed_deal_json || {};
  const attached: any[] = Array.isArray(deal.attached_rowids) ? deal.attached_rowids : [];
  if (attached.length > 0) {
    return attached.map((item: any, i: number) => {
      const label = (typeof item === 'object' && item.label)
        ? item.label
        : (i === 0 ? '主証憑' : `添付 ${i}`);
      // Explicit `url` field takes priority (used for upright-corrected PDFs
      // hosted on Pages). Otherwise derive ARICA proxy URL from rowid.
      if (typeof item === 'object' && item.url) return { url: item.url, label };
      const rowid = typeof item === 'object' ? item.rowid : item;
      return {
        url: `https://invoice-review.system-0f7.workers.dev/pdf/${rowid}`,
        label,
      };
    });
  }
  if (p.pdf_rowid) {
    const ridInt = parseInt(String(p.pdf_rowid), 10);
    if (Number.isFinite(ridInt) && ridInt > 0) {
      return [{
        url: `https://invoice-review.system-0f7.workers.dev/pdf/${ridInt}`,
        label: '主証憑',
      }];
    }
  }
  return [];
}

// Convert a DealProposal (from API) into a JournalEntry for display in the main table.
function proposalToEntry(p: DealProposal): JournalEntry {
  const deal: any = p.proposed_deal_json || {};
  const details: any[] = Array.isArray(deal.details) ? deal.details : [];
  const firstSection = details[0]?.sectionName || details[0]?.section_name || '—';
  // Map proposal status to the table's VerificationStatus type.
  const status: VerificationStatus =
    p.status === 'registered' ? 'approved'
    : p.status === 'approved' ? 'approved'
    : p.status === 'rejected' ? 'blocked'
    : 'needs_review'; // 'proposed' → needs_review so user sees it needs action
  return {
    id: `proposal_${p.id}`,
    rowid: parseInt(String(p.pdf_rowid || '0'), 10) || 0,
    refNumber: p.ref_number,
    partnerName: p.partner_name || `partner_id: ${p.partner_id}`,
    partnerId: p.partner_id,
    documentType: 'invoice',
    verifiedType: 'invoice',
    issueDate: p.issue_date || '',
    dueDate: p.due_date || '',
    grandTotal: p.amount || details.reduce((s: number, d: any) => s + (d.amount || 0), 0),
    taxBreakdown: [],
    details: details.map((d: any, idx: number) => ({
      index: idx,
      accountName: d.accountName || d.account_item_name || d.account_name || (d.account_item_id || d.accountItemId ? `#${d.account_item_id || d.accountItemId}` : '—'),
      accountItemId: d.account_item_id || d.accountItemId || 0,
      taxCode: d.tax_code || d.taxCode || 0,
      taxCodeName: d.taxCodeName || d.tax_code_name || '',
      sectionName: d.sectionName || d.section_name || '',
      sectionId: d.section_id || d.sectionId || 0,
      itemName: d.itemName || d.item_name || null,
      amount: d.amount || 0,
      taxAmount: d.taxAmount || d.tax_amount || 0,
      description: d.description || '',
      mappingSource: null,
    })),
    storeName: (() => {
      const uniq = Array.from(new Set(
        details.map((d: any) => d.sectionName || d.section_name).filter(Boolean),
      ));
      if (uniq.length === 1) return uniq[0];
      if (uniq.length > 1) return `${uniq.length}店舗按分`;
      return firstSection;
    })(),
    status,
    alreadyRegistered: p.status === 'registered',
    registeredDealId: p.freee_deal_id ?? null,
    confidence: (() => {
      const v42 = (p.audit_summary && (p.audit_summary as any).v42) || null;
      if (v42 && typeof v42.final_credibility === 'number') return v42.final_credibility;
      return 1.0;
    })(),
    flags: (() => {
      const base: string[] = [];
      if (p.status === 'proposed') base.push('要承認');
      else if (p.status === 'rejected') base.push('却下済');
      // P11 (2026-05-20): v4.2 credibility chip
      const v42 = (p.audit_summary && (p.audit_summary as any).v42) || null;
      if (v42) {
        const cred = v42.final_credibility;
        if (typeof cred === 'number') {
          if (cred >= 0.90) base.push(`✓ cred=${cred.toFixed(2)}`);
          else if (cred >= 0.70) base.push(`⚠ cred=${cred.toFixed(2)}`);
          else base.push(`⚠⚠ cred=${cred.toFixed(2)}`);
        }
        if (v42.agree_count != null) base.push(`agree=${v42.agree_count}/3`);
        if (v42.decision === 'adopt_with_warning') base.push('R6 解決');
        if (v42.carryover && v42.carryover.detected) base.push('繰越検出');
      }
      // P13 dedup warning chip
      const dedup = p.audit_summary && (p.audit_summary as any).dedup_warning;
      if (dedup && dedup.candidate_count > 0) {
        base.push(`⛔ 二重計上候補 ${dedup.candidate_count}件`);
      }
      return base;
    })(),
    notes: p.rejection_reason || p.notes || '',
    pdfUrl: (() => {
      const n = p.pdf_rowid ? parseInt(String(p.pdf_rowid), 10) : NaN;
      return Number.isFinite(n) && n > 0 ? `https://invoice-review.system-0f7.workers.dev/pdf/${n}` : '';
    })(),
    tNumber: null,
    invoiceNumber: null,
    patternStatus: 'Reviewed',
    a2aAgreement: true,
    dialogueRounds: 0,
  };
}

// ---------------------------------------------------------------------------
// Merge fetched proposals into the current entries[] (2026-06-16 抽出)。
//   - deferred 行は同 rowid の proposal が来たら drop (proposal が prevail)。
//   - 既に表示中の ref を持つ proposal は重複させない。
//   - 楽観的更新: local status が server より先行している間は local を保持
//     (= 承認直後に poll が needs_review へ巻き戻すのを防止)。
// poll (差分検知) と 手動 flush (反映時の再計算) の両方で使う = 反映が常に
// 「その時点の表示中 entries」基準になり、 staging 後の局所操作も失わない。
// ---------------------------------------------------------------------------
function mergeProposalsIntoEntries(
  prev: JournalEntry[],
  proposalEntries: JournalEntry[],
): JournalEntry[] {
  const rank = (s: string): number =>
    ({ blocked: -1, pending: 0, needs_review: 1, approved: 2 }[s] ?? 0);
  const staticRows = prev.filter((e) => !e.id.startsWith('proposal_'));
  const incomingRowids = new Set(proposalEntries.map((e) => e.rowid));
  const filteredStatic = staticRows.filter(
    (e) => !(e.id.startsWith('deferred_') && incomingRowids.has(e.rowid)),
  );
  const existingRefs = new Set(filteredStatic.map(entryRefNumber));
  const prevProposalById = new Map(
    prev.filter((e) => e.id.startsWith('proposal_')).map((e) => [e.id, e]),
  );
  const freshProposals = proposalEntries
    .filter((e) => !existingRefs.has(entryRefNumber(e)))
    .map((fresh) => {
      const local = prevProposalById.get(fresh.id);
      if (!local) return fresh;
      if (fresh.alreadyRegistered) return fresh; // 登録済は常に採用 (✓登録済 表示)
      if (rank(local.status) > rank(fresh.status)) {
        return { ...fresh, status: local.status }; // local 先行 → 保持
      }
      return fresh;
    });
  return [...filteredStatic, ...freshProposals];
}

// ---------------------------------------------------------------------------
// Deferred → JournalEntry conversion (2026-05-11 Team V — radical merge)
// ---------------------------------------------------------------------------
// 「未処理 deferred」 は status から完全削除。 主 list に汎用 `needs_review` として
// 透過 merge され、 視覚的に proposal 行と区別不能。 defer_reason は flags[] に
// 残し、 side panel の フラグ表示 / requeue・resolve action から確認可能。
const DEFER_REASON_LABEL: Record<string, string> = {
  pdf_extract_failed: 'PDF抽出失敗',
  partner_id_null: '取引先未解決',
  bank_auto_debit: '口座引落 (対象外)',
  no_recommend_data: '学習データなし',
  other: 'その他',
};

function deferredToEntry(d: DeferredInvoiceItem): JournalEntry {
  const partnerLabel =
    d.partner_name ||
    (d.partner_id ? `partner_id: ${d.partner_id}` : '取引先未解決');
  const reasonFlag = DEFER_REASON_LABEL[d.defer_reason] || d.defer_reason;
  return {
    id: `deferred_${d.id}`,
    rowid: d.arica_rowid,
    refNumber: `arica:${d.arica_rowid}`,
    partnerName: partnerLabel,
    partnerId: d.partner_id || 0,
    documentType: 'invoice',
    verifiedType: 'invoice',
    issueDate: '',
    dueDate: '',
    grandTotal: d.total_amount || 0,
    taxBreakdown: [],
    details: [],
    storeName: '—',
    // Team V: 汎用 status (= proposal 行と区別不能)。 requeue/resolve は side panel 経由 (= deferredByEntryId lookup)
    status: 'needs_review',
    alreadyRegistered: false,
    registeredDealId: null,
    confidence: 0,
    flags: [reasonFlag],
    notes: d.defer_detail || d.notes || '',
    pdfUrl: `https://invoice-review.system-0f7.workers.dev/pdf/${d.arica_rowid}`,
    tNumber: null,
    invoiceNumber: null,
    patternStatus: 'Unknown',
    a2aAgreement: false,
    dialogueRounds: 0,
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// 表示名は統一語彙 (lib/statusVocab.ts) に追随。value は内部status据置 (2026-07-08)
const STATUS_OPTIONS = [
  { name: 'すべて', value: 'all' },
  { name: '処理中', value: 'pending' },
  { name: '完了(承認済)', value: 'approved' },
  { name: 'あなたの対応待ち', value: 'needs_review' },
  { name: '除外(却下)', value: 'blocked' },
];

const DEPT_OPTIONS = [
  { name: 'すべての部門', value: 'all' },
  { name: '本部', value: '本部' },
  { name: 'Bistrot Gourmand', value: 'Bistrot Gourmand' },
  { name: 'La SOFFITTA', value: 'La SOFFITTA' },
  { name: '人間関係', value: '人間関係' },
  { name: '信州屋 渋谷店', value: '信州屋 渋谷店' },
  { name: '信州屋 新宿南口店', value: '信州屋 新宿南口店' },
  { name: 'BiOcafe', value: 'BiOcafe' },
  { name: '山城屋庄藏', value: '山城屋庄藏' },
];

const PATTERN_STATUS_LABELS: Record<string, { label: string; bg: string; color: string }> = {
  Trusted:   { label: '信頼済',   bg: '#e6f4ea', color: '#338650' },
  Reviewed:  { label: 'レビュー済', bg: '#e8f0fe', color: '#2e7cf6' },
  Candidate: { label: '候補',     bg: '#fef7e0', color: '#c67320' },
  Unknown:   { label: '未学習',   bg: '#f5f3f2', color: '#8a8580' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const statusToIcon = (status: VerificationStatus): 'success' | 'warning' | 'error' | 'progress' | 'required' => {
  switch (status) {
    case 'approved': return 'success';
    case 'needs_review': return 'warning';
    case 'blocked': return 'error';
    default: return 'progress';
  }
};

// 統一語彙 (lib/statusVocab.ts) に委譲 (2026-07-08)。deferred由来は「対応待ち(エラー対応)」、
// proposal由来は「対応待ち(承認)」、修正指示送信済は「処理中(指示対応中)」に分岐する。
const statusToLabel = (status: VerificationStatus, entryId?: string, hasSentInstruction?: boolean): string =>
  verificationStatusToUnified(status, entryId, hasSentInstruction).label;

const fmt = (n: number) => '¥' + n.toLocaleString('ja-JP');

const confidenceColor = (c: number) =>
  c >= 0.92 ? '#338650' : c >= 0.75 ? '#c67320' : '#c33939';

const confidenceBarColor = (c: number) =>
  c >= 0.92 ? '#338650' : c >= 0.75 ? '#c67320' : '#c33939';

// ---- Month selector helpers (schema-v20, 2026-04-22) -----------------------
// `#invoice/YYYY-MM` の hash → state 同期 / state → hash 書き込み
function parseMonthHash(): string | null {
  if (typeof window === 'undefined') return null;
  const h = window.location.hash.toLowerCase();
  const m = h.match(/invoice\/(\d{4})-(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}`;
}

// `#invoice/YYYY-MM?ref=arica:{rowid}` の ref 抽出 (2026-07-08 run台帳からのディープリンク受け)。
// hash 内の `?` 以降を手動パースする (location.search は hash 内クエリを拾わないため)。
function parseRefFromHash(): string | null {
  if (typeof window === 'undefined') return null;
  const h = window.location.hash;
  const q = h.indexOf('?');
  if (q === -1) return null;
  const m = h.slice(q + 1).match(/(?:^|&)ref=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function currentYyyyMm(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthOffset(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Styles (CSS-in-JS objects)
// ---------------------------------------------------------------------------

const S = {
  page: {
    background: '#f7f5f5',
    minHeight: '100vh',
    paddingBottom: '3rem',
  } as React.CSSProperties,

  // KPI card grid (2026-05-11 Team V: 5 列 — 「未処理 (Deferred)」 カード削除)
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  } as React.CSSProperties,

  kpiCard: {
    position: 'relative' as const,
    overflow: 'hidden',
  } as React.CSSProperties,

  kpiAccent: (color: string) => ({
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '3px',
    height: '100%',
    background: color,
    borderRadius: '3px 0 0 3px',
  }),

  // Two-column layout
  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '0',
    transition: 'grid-template-columns 0.25s ease',
  } as React.CSSProperties,

  mainLayoutWithPanel: {
    display: 'grid',
    gridTemplateColumns: '62% 38%',
    gap: '16px',
    alignItems: 'stretch', // right column grows tall enough for sticky to remain visible
  } as React.CSSProperties,

  // Table header
  thCell: {
    padding: '10px 12px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap' as const,
    borderBottom: '2px solid #dbd9d7',
    background: '#fafaf8',
  } as React.CSSProperties,

  // Table data cell
  tdCell: {
    padding: '12px 12px',
    fontSize: '0.8125rem',
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #edebea',
  } as React.CSSProperties,

  // Row styles. 2026-05-11 Team V: deferred 区別を視覚的にも完全廃止 (= 通常 row と同等)
  row: (isSelected: boolean, isActive: boolean) => ({
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    background: isActive
      ? '#ebf3ff'
      : isSelected
        ? '#f0f8ff'
        : 'transparent',
    borderLeft: '3px solid transparent',
  }),

  // Badge
  badge: (bg: string, color: string) => ({
    display: 'inline-block',
    fontSize: '0.625rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
    lineHeight: '1.4',
    background: bg,
    color: color,
    whiteSpace: 'nowrap' as const,
  }),

  // Flag badge
  flagBadge: {
    display: 'inline-block',
    fontSize: '0.5625rem',
    fontWeight: 600,
    padding: '1px 7px',
    borderRadius: '10px',
    background: '#fff3e0',
    color: '#c67320',
    marginRight: '3px',
    marginTop: '2px',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  // Confidence bar background
  confidenceTrack: {
    width: '100%',
    height: '4px',
    background: '#edebea',
    borderRadius: '2px',
    marginTop: '3px',
  } as React.CSSProperties,

  confidenceFill: (pct: number, color: string) => ({
    width: `${pct}%`,
    height: '4px',
    background: color,
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  }),

  // Expand section
  expandSection: {
    padding: '16px 16px 16px 48px',
    background: '#fafaf8',
    borderBottom: '2px solid #edebea',
  } as React.CSSProperties,

  // Detail table
  detailTh: {
    padding: '6px 10px',
    fontSize: '0.625rem',
    fontWeight: 600,
    color: '#8a8580',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    borderBottom: '1px solid #dbd9d7',
    background: '#f7f5f5',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  detailTd: {
    padding: '8px 10px',
    fontSize: '0.75rem',
    borderBottom: '1px solid #edebea',
    verticalAlign: 'top' as const,
  } as React.CSSProperties,

  // Side panel — sticky pins it to the viewport top while scrolling the table.
  sidePanel: {
    position: 'sticky' as const,
    top: '12px',
    maxHeight: 'calc(100vh - 24px)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden',
    background: '#fff',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    border: '1px solid #e5e4e2',
  } as React.CSSProperties,

  sidePanelHeader: {
    padding: '16px 20px 12px',
    borderBottom: '1px solid #edebea',
    background: '#fff',
  } as React.CSSProperties,

  sidePanelBody: {
    flex: 1,
    overflowY: 'auto' as const,
    background: '#fff',
  } as React.CSSProperties,

  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '6px 0',
    borderBottom: '1px solid #f5f3f2',
    fontSize: '0.75rem',
  } as React.CSSProperties,

  metaLabel: {
    color: '#8a8580',
    fontSize: '0.6875rem',
    flexShrink: 0,
    marginRight: '12px',
  } as React.CSSProperties,

  metaValue: {
    color: '#252525',
    fontSize: '0.75rem',
    fontWeight: 500,
    textAlign: 'right' as const,
    wordBreak: 'break-all' as const,
  } as React.CSSProperties,

  // Filter bar
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,

  // Summary footer
  summaryFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    marginTop: '8px',
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SentInstructionInfo {
  instructionId: string;
  action: CorrectionAction;
  at: string; // ISO timestamp
}

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'notice' | 'info';
  text: string;
}

// -----------------------------------------------------------------------------
// 単体行承認のリアルタイム登録進捗 (2026-07-10)
//   user 指摘「行承認しても表記が即『完了』になるだけで実際は未登録・進捗も見えない」対応。
//   approve 応答の batch_id (worker auto_enroll) を受けて、その行に進捗チップ
//   「登録キュー投入中→登録中→照合中→登録済み(緑)/失敗(赤)」を表示し、
//   fetchBatchDetail を 5s ポーリングして batch item の実 state に追随する。
//   楽観の「即・登録済み」表示は廃止 (= verified 到達で初めて ✓登録済 になる)。
// -----------------------------------------------------------------------------
type RowRegPhase = 'queueing' | 'registering' | 'verifying' | 'failed';
interface RowRegProgress {
  phase: RowRegPhase;
  ref: string;
  batchId?: string;
  error?: string;
}

const ROW_REG_CHIP: Record<RowRegPhase, { label: string; bg: string; color: string; spin: boolean }> = {
  queueing:    { label: '登録キュー投入中…', bg: '#e8f0fe', color: '#1f5fc4', spin: true },
  registering: { label: '登録中…',           bg: '#e8f0fe', color: '#1f5fc4', spin: true },
  verifying:   { label: '照合中…',           bg: '#f3e8fd', color: '#7c3aed', spin: true },
  failed:      { label: '登録失敗',          bg: '#fdecea', color: '#b3261e', spin: false },
};

// 一括承認フルスクリーンオーバーレイの進捗状態 (2026-07-10)
//   approving = 承認POST実行中 (承認中 n/N)
//   enrolling = バッチ完全性台帳へ enroll 中 (成功で #batches/{id} へ遷移)
//   error     = enroll失敗 or 全件承認失敗 — console でなく画面上に大きく表示し [再試行] を出す
//               (前回 console-only で enroll 失敗が見えなかった実事故対応)
interface BulkApproveProgress {
  phase: 'approving' | 'enrolling' | 'error';
  done: number;
  total: number;
  failed: number;          // 承認失敗件数 (該当行は自動で元ステータスに rollback 済)
  approvedRefs: string[];  // enroll 対象 ref_number 一覧 (再試行用に保持)
  errorText?: string;
}

export const JournalVerificationPage: React.FC = () => {
  // State
  const [searchText, setSearchText] = useState('');
  // 2026-05-11 Team V: 'deferred' filter は廃止 → 常に 'all' init
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [deptFilter, setDeptFilter] = useState('all');
  // 2026-04-24: 支払月ソート (user 要望)
  const [sortKey, setSortKey] = useState<'dueDate' | 'issueDate' | 'amount' | 'partnerName'>('dueDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // schema-v20 — 支払月 (target_payment_month) セレクタ。'all' = legacy 互換 (全月)
  const [targetPaymentMonth, setTargetPaymentMonth] = useState<string>(() => parseMonthHash() || 'all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [sidePanelTab, setSidePanelTab] = useState(0);
  // entry.id → DealProposal lookup so we can render multi-receipt tabs.
  const [proposalByEntryId, setProposalByEntryId] = useState<Record<string, DealProposal>>({});
  // entry.id (= `deferred_N`) → DeferredInvoiceItem lookup (= 詳細 panel / requeue / resolve 用)
  const [deferredByEntryId, setDeferredByEntryId] = useState<Record<string, DeferredInvoiceItem>>({});
  // Declared early so effects that depend on it can read without TDZ errors.
  const [correctionEntryId, setCorrectionEntryId] = useState<string | null>(null);
  // 2026-07-09 (笠間商事 "(858355854)" incident): 勘定科目の ID素通し表示防止。
  //   accountName が生 ID 文字列の場合に account_items_cache マップで名前解決する第二防衛線。
  const [accountItemsMap, setAccountItemsMap] = useState<Map<number, string> | null>(null);
  useEffect(() => {
    let alive = true;
    fetchAccountItemsMap().then((m) => { if (alive) setAccountItemsMap(m); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // 2026-06-16: 「データ無変化なら再描画しない / 変化は手動更新ボタンで反映」対応。
  //   - entriesRef: poll が現在表示中の entries を同期的に読むためのミラー (setState 待ちにしない)。
  //   - pendingProposalLookup: 差分検知時、ビューへ自動反映せず保留する次データの lookup。
  //   - proposalUpdateCount: 変化した proposal 件数 (新規/status変化/金額変化/消滅) = バッジ表示数。
  //   - proposalsInitializedRef: 初回ロード判定 (false の間は自動反映、以後は手動更新ボタン経由)。
  const entriesRef = useRef<JournalEntry[]>([]);
  // サーバ proposals の「内容シグネチャ」(id+status+amount+freee_deal_id を結合・ソート)。
  // 直近取得と一致なら state を一切更新せず再描画を抑止する。
  const lastProposalSigRef = useRef<string>('');
  // deferred poll も無変化なら setState せず再描画を抑止 (30s ごとの行リセット防止)。
  const lastDeferredSigRef = useRef<string>('');
  const [pendingProposalLookup, setPendingProposalLookup] = useState<Record<string, DealProposal> | null>(null);
  const [proposalUpdateCount, setProposalUpdateCount] = useState<number>(0);
  const proposalsInitializedRef = useRef<boolean>(false);

  // hashchange listener for #invoice/YYYY-MM ⇔ state sync + month-switch reset
  useEffect(() => {
    const onHashChange = () => {
      const m = parseMonthHash();
      setTargetPaymentMonth(m ?? 'all');
      setHighlightRef(parseRefFromHash());
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // ---- run台帳からのディープリンク (?ref=arica:{rowid}) 受け (2026-07-08) ----
  const [highlightRef, setHighlightRef] = useState<string | null>(() => parseRefFromHash());
  const [highlightEntryId, setHighlightEntryId] = useState<string | null>(null);
  const highlightHandledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightRef || entries.length === 0) return;
    if (highlightHandledRef.current === highlightRef) return;
    highlightHandledRef.current = highlightRef;
    const rid = highlightRef.replace(/^arica:/, '');
    const hit = entries.find((e) => e.refNumber === highlightRef || String(e.rowid) === rid);
    if (hit) {
      setHighlightEntryId(hit.id);
      setTimeout(() => document.getElementById(`entry-row-${hit.id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 120);
      setTimeout(() => setHighlightEntryId(null), 2600);
    } else {
      pushToast({
        kind: 'error',
        text: `対象の仕訳 (${highlightRef}) が見つかりません。承認済み・月フィルタ違い・未生成の可能性があります。取込run台帳 (#invoice_runs) で状態を確認してください。`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightRef, entries]);

  // 2026-05-13: page-level sub tabs (= 仕訳検証 / 受信一覧 / 口座引落). Hash:
  //   #invoice              → 仕訳検証 (default)
  //   #invoice/coverage     → 受信一覧
  //   #invoice/bank_auto_debit → 口座引落請求書 (2026-05-19 統合)
  //   legacy #bank_auto_debit_invoice も backward compat で 口座引落 tab を 選択
  type PageSubTab = 'verify' | 'coverage' | 'bank_auto_debit';
  const SUB_TABS: PageSubTab[] = ['verify', 'coverage', 'bank_auto_debit'];
  const SUB_TAB_LABELS = ['仕訳検証', '受信一覧', '口座引落'];

  const resolveTabFromHash = (h: string): PageSubTab => {
    if (h.startsWith('#invoice/coverage')) return 'coverage';
    if (h.startsWith('#invoice/bank_auto_debit')) return 'bank_auto_debit';
    if (h.startsWith('#bank_auto_debit')) return 'bank_auto_debit';
    return 'verify';
  };

  const [pageSubTab, setPageSubTab] = useState<PageSubTab>(() => {
    const h = typeof window !== 'undefined' ? window.location.hash : '';
    return resolveTabFromHash(h);
  });

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      setPageSubTab(resolveTabFromHash(h));
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const onChangeSubTab = useCallback((i: number) => {
    const next = SUB_TABS[i];
    setPageSubTab(next);
    const newHash =
      next === 'coverage' ? '#invoice/coverage'
      : next === 'bank_auto_debit' ? '#invoice/bank_auto_debit'
      : '#invoice';
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, []);

  // entries の最新値を ref にミラー。 proposals poll が setState 完了を待たずに
  // 「現在表示中の行」を読み、 次データとの差分を同期的に判定できるようにする。
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  // 2026-05-11 Team V: deferred items を 主 entries[] に汎用 `needs_review` で透過 merge する poll。
  //   UI 上 deferred という区別は 一切表示しない (= status / KPI / filter / URL / 視覚 差別なし)。
  //   30秒間隔。 deferred 詳細 (= requeue / resolve) は side panel の actions で実行可能。
  useEffect(() => {
    let cancelled = false;
    const reload = async () => {
      try {
        // 未処理 (open) のみ 主 list に表示する。 解決済は履歴で別途確認 (= 詳細 panel)。
        const r = await fetchDeferredInvoices({ status: 'open', limit: 200 });
        if (cancelled) return;
        const items = Array.isArray(r.items) ? r.items : [];
        // 無変化なら setState せず終了 (= 30s ごとの不要な再描画/行リセットを抑止)。
        const dsig = items
          .map((d) => `${d.id}|${d.defer_reason}|${d.partner_id ?? ''}|${d.total_amount ?? ''}`)
          .sort()
          .join('\n');
        if (dsig === lastDeferredSigRef.current) return;
        lastDeferredSigRef.current = dsig;
        const deferredEntries = items.map(deferredToEntry);
        const lookup: Record<string, DeferredInvoiceItem> = {};
        items.forEach((d) => { lookup[`deferred_${d.id}`] = d; });
        setDeferredByEntryId(lookup);
        setEntries((prev) => {
          // 既存 deferred 行 を除去 → 新規 deferred 行 を append。 重複は arica_rowid で proposal 行と区別 (= id prefix が `deferred_`)。
          const nonDeferred = prev.filter((e) => !e.id.startsWith('deferred_'));
          // proposal 行 と arica_rowid が衝突する deferred 行は skip (= proposal が prevail)
          const proposalRowids = new Set(
            nonDeferred
              .filter((e) => e.id.startsWith('proposal_'))
              .map((e) => e.rowid)
          );
          const dedupedDeferred = deferredEntries.filter(
            (de) => !proposalRowids.has(de.rowid)
          );
          return [...nonDeferred, ...dedupedDeferred];
        });
      } catch (e) {
        // 失敗時は静かに skip
        console.error('[FREEEMAN] deferred fetch failed:', e);
      }
    };
    reload();
    const t = setInterval(reload, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Reset selection / expansion / side panel on month switch (Reader A note).
  // Also clear entries so a stale list from the previous month does not flash
  // for 8s (the next reload() fires immediately via the dep-change below).
  // 2026-05-13: deferred rows は month 非依存なので保持。
  useEffect(() => {
    setSelectedIds(new Set());
    setExpandedId(null);
    setActivePanelId(null);
    setEntries((prev) => prev.filter((e) => !e.id.startsWith('proposal_')));
    // 月切替は明示操作 → 次の proposals ロードは「初回」扱いで自動反映し、
    // 保留中の更新バッジも破棄する (前月の差分を持ち越さない)。
    proposalsInitializedRef.current = false;
    lastProposalSigRef.current = '';
    setPendingProposalLookup(null);
    setProposalUpdateCount(0);
  }, [targetPaymentMonth]);

  // proposals lookup をビューへ反映 (= 初回ロード / 手動「表示を更新」押下時)。
  //   反映は常に「その時点の表示中 entries (entriesRef)」基準で再 merge するため、
  //   staging 後にユーザーが行った局所操作 (承認/却下) を取りこぼさない。
  const applyProposalUpdate = useCallback((lookup: Record<string, DealProposal>) => {
    // 2026-06-16: 反映でリスト高さが変動しても scroll 位置を保持 (= 下にスクロール
    //   した状態で更新しても画面トップへ戻されない)。
    const keepY = window.scrollY;
    const proposalEntries = Object.values(lookup).map(proposalToEntry);
    setEntries(mergeProposalsIntoEntries(entriesRef.current, proposalEntries));
    setProposalByEntryId(lookup);
    setPendingProposalLookup(null);
    setProposalUpdateCount(0);
    requestAnimationFrame(() => window.scrollTo(0, keepY));
  }, []);

  // 保留中の更新を手動でビューへ反映する (バナー / 更新ボタンから呼ぶ)。
  const flushPendingProposalUpdate = useCallback(() => {
    if (pendingProposalLookup) {
      applyProposalUpdate(pendingProposalLookup);
    }
  }, [pendingProposalLookup, applyProposalUpdate]);

  // On mount and every 30s: pull deal_proposals (all statuses) and merge them.
  // PAUSED while the correction modal is open so in-progress edits aren't wiped.
  // 2026-06-16 UX 修正 (数秒ごとに画面が勝手に更新され閲覧不能になる問題):
  //   1) サーバ proposals の内容が直近取得と同一 → state を一切更新せず再描画なし。
  //   2) 表示中の行と差異あり → ビューへ自動反映せず pending に保持し「更新あり (N件)」
  //      バッジを表示。 ユーザーが「表示を更新」押下時に初めてビューへ反映。
  //   ※ 初回ロードのみ自動反映。 承認/却下の楽観的更新 (rank) は維持し、
  //      server が古い status を返す間は「変化」と看做さずバッジ誤表示しない。
  useEffect(() => {
    let cancelled = false;

    const reload = async () => {
      // Don't clobber the form while user is editing. Also freeze view updates.
      if (correctionEntryId) return;
      try {
        const statuses = ['proposed', 'approved', 'rejected', 'registered'] as const;
        const all: DealProposal[] = [];
        for (const s of statuses) {
          try {
            // Phase 4-A (2026-05-11): bank_auto_debit (口座引落) を別 page に分離。
            //   JournalVerificationPage は 25日払い通常請求書のみ表示 (= flow_type='exclude_bank_auto_debit')
            const r = targetPaymentMonth === 'all'
              ? await fetchDealProposals(s, { flow_type: 'exclude_bank_auto_debit' })
              : await fetchDealProposalsByTargetMonth(targetPaymentMonth, s, { flow_type: 'exclude_bank_auto_debit' });
            all.push(...r.items);
          } catch (e) { /* skip one status, keep others */ }
        }
        if (cancelled) return;
        if (all.length === 0) return;

        // (1) サーバ内容シグネチャ: 各 proposal の id+status+amount+freee_deal_id を
        //     結合・ソート。 直近と一致なら サーバ側に変化なし → 完全 no-op (再描画なし)。
        const sig = all
          .map((p) => `${p.id}|${p.status}|${p.amount}|${p.freee_deal_id ?? ''}`)
          .sort()
          .join('\n');
        if (sig === lastProposalSigRef.current) return;
        lastProposalSigRef.current = sig;

        const proposalEntries = all.map(proposalToEntry);
        const lookup: Record<string, DealProposal> = {};
        all.forEach((p) => { lookup[`proposal_${p.id}`] = p; });

        // 現在表示中の entries (ref 経由 = setState 完了待ち不要) を prev として merge。
        const prev = entriesRef.current;
        const nextEntries = mergeProposalsIntoEntries(prev, proposalEntries);

        // 初回ロードは自動反映 (画面を最初に埋める)。
        if (!proposalsInitializedRef.current) {
          proposalsInitializedRef.current = true;
          applyProposalUpdate(lookup);
          return;
        }

        // (2) 表示用 diff: 現在表示中の proposal 行 と merge 後 proposal 行を
        //     (id, status, amount, registeredDealId) で比較。 楽観的更新 (rank) 反映後の
        //     値で比べるため、 server が古い status を返している間は「変化」にならない
        //     (= 承認直後のバッジ誤表示を防止)。
        const sigOf = (e: JournalEntry) =>
          `${e.id}|${e.status}|${e.grandTotal}|${e.registeredDealId ?? ''}`;
        const prevProposalSigs = new Map(
          prev.filter((e) => e.id.startsWith('proposal_')).map((e) => [e.id, sigOf(e)]),
        );
        const nextProposalRows = nextEntries.filter((e) => e.id.startsWith('proposal_'));
        let changed = 0;
        const seen = new Set<string>();
        for (const e of nextProposalRows) {
          seen.add(e.id);
          if (prevProposalSigs.get(e.id) !== sigOf(e)) changed += 1; // 新規 or status/金額 変化
        }
        Array.from(prevProposalSigs.keys()).forEach((id) => {
          if (!seen.has(id)) changed += 1; // 消滅した行
        });

        if (changed === 0) {
          // 表示上の差異なし (= 楽観的更新で吸収済 / 非表示フィールドのみ変化) → 何もしない。
          return;
        }

        // 差異あり → ビューへ自動反映せず pending に保持し、 バッジで通知。
        setPendingProposalLookup(lookup);
        setProposalUpdateCount(changed);
      } catch (e) {
        console.error('[FREEEMAN] proposals fetch failed:', e);
      }
    };

    reload();
    const t = setInterval(reload, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [correctionEntryId, targetPaymentMonth, applyProposalUpdate]);

  // Phase 3 state
  const [activityOpen, setActivityOpen] = useState<boolean>(false);
  const [batchRegisterOpen, setBatchRegisterOpen] = useState<boolean>(false);
  const [proposalsPanelOpen, setProposalsPanelOpen] = useState<boolean>(false);
  const [receiptPanelOpen, setReceiptPanelOpen] = useState<boolean>(false);
  const [cadencePanelOpen, setCadencePanelOpen] = useState<boolean>(false);
  const [pendingProposalCount, setPendingProposalCount] = useState<number>(0);
  const [batchRegistering, setBatchRegistering] = useState<boolean>(false);
  const [batchResultToast, setBatchResultToast] = useState<string | null>(null);
  const [sentInstructions, setSentInstructions] = useState<Record<string, SentInstructionInfo>>({});
  const [toasts, setToasts] = useState<Toast[]>([]);
  // 一括承認オーバーレイ (グレーアウト+リアルタイム進捗)。null = 非表示
  const [bulkProgress, setBulkProgress] = useState<BulkApproveProgress | null>(null);

  // ---- 単体行承認の登録進捗 (2026-07-10) ------------------------------------
  //   entry.id → RowRegProgress。 verified 到達で map から削除し、行は実データ由来の
  //   「✓ 登録済」表示に引き継ぐ (= 楽観の「即・登録済み」廃止)。
  const [rowRegByEntryId, setRowRegByEntryId] = useState<Record<string, RowRegProgress>>({});
  const rowPollTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const clearRowPoll = useCallback((entryId: string) => {
    const t = rowPollTimersRef.current.get(entryId);
    if (t !== undefined) {
      clearTimeout(t);
      rowPollTimersRef.current.delete(entryId);
    }
  }, []);
  // unmount 時に全行ポーリングを停止 (バッチ自体は worker 側で進行し #batches で追跡可能)
  useEffect(() => {
    const timers = rowPollTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // batch item state を 5s ポーリングし、行チップを実 state に追随させる。
  //   pending/registering → 登録中 / posted → 照合中 / verified → 登録済み(ポーリング停止) /
  //   failed・verify_failed → 失敗(赤・ポーリング停止)。
  const pollBatchForRow = useCallback((entryId: string, ref: string, batchId: string) => {
    const MAX_ATTEMPTS = 120; // 5s × 120 = 最長10分。以降は #batches で追跡 (チップtitleに明記済)
    let attempts = 0;
    const tick = async () => {
      rowPollTimersRef.current.delete(entryId);
      attempts += 1;
      try {
        const r = await fetchBatchDetail(batchId);
        const item = (r.items || []).find((it) => it.target_ref === ref);
        const st = item?.state;
        if (st === 'verified') {
          // 実 state が verified に到達 → チップ削除し、本当の「✓ 登録済」表示へ。
          setRowRegByEntryId((prev) => {
            const next = { ...prev };
            delete next[entryId];
            return next;
          });
          setEntries((prev) => prev.map((e) => (e.id === entryId
            ? {
                ...e,
                status: 'approved' as const,
                alreadyRegistered: true,
                registeredDealId: item?.freee_deal_id ?? e.registeredDealId,
              }
            : e)));
          return;
        }
        if (st === 'failed' || st === 'verify_failed') {
          let errText: string | undefined;
          if (item?.error_json) {
            try {
              const ej = JSON.parse(item.error_json);
              errText = ej?.message || ej?.error || item.error_json;
            } catch {
              errText = item.error_json;
            }
          }
          setRowRegByEntryId((prev) => ({
            ...prev,
            [entryId]: { phase: 'failed', ref, batchId, error: errText },
          }));
          return;
        }
        // pending / registering → 登録中、 posted → 照合中
        const phase: RowRegPhase = st === 'posted' ? 'verifying' : 'registering';
        setRowRegByEntryId((prev) => {
          const cur = prev[entryId];
          if (cur && cur.phase === phase && cur.batchId === batchId) return prev; // 無変化なら再描画しない
          return { ...prev, [entryId]: { phase, ref, batchId } };
        });
      } catch (e) {
        // 一時的な通信失敗は無視して次 tick で再試行
        console.warn('[row-reg-poll] fetchBatchDetail failed (retrying):', batchId, e);
      }
      if (attempts >= MAX_ATTEMPTS) {
        console.warn('[row-reg-poll] max attempts reached — 以降は #batches で追跡:', batchId, ref);
        return;
      }
      rowPollTimersRef.current.set(entryId, setTimeout(tick, 5000));
    };
    tick();
  }, []);

  // Derived
  const filtered = useMemo(() => {
    const list = entries.filter((e) => {
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (deptFilter !== 'all' && e.storeName !== deptFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const searchable = [e.partnerName, e.storeName, e.invoiceNumber, e.tNumber]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      return true;
    });
    // 2026-04-24 sort: user 要望「支払月ソート」対応
    const sorted = [...list].sort((a, b) => {
      let va: string | number = '', vb: string | number = '';
      switch (sortKey) {
        case 'dueDate':     va = a.dueDate || '';   vb = b.dueDate || '';   break;
        case 'issueDate':   va = a.issueDate || ''; vb = b.issueDate || ''; break;
        case 'amount':      va = a.grandTotal;      vb = b.grandTotal;      break;
        case 'partnerName': va = a.partnerName || ''; vb = b.partnerName || ''; break;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [entries, statusFilter, deptFilter, searchText, sortKey, sortDir]);

  const stats = useMemo(() => {
    const total = entries.length;
    const approved = entries.filter((e) => e.status === 'approved').length;
    const pending = entries.filter((e) => e.status === 'pending').length;
    const needsReview = entries.filter((e) => e.status === 'needs_review').length;
    const blocked = entries.filter((e) => e.status === 'blocked').length;
    // deferred由来 (id が 'deferred_' 前置) = 対応待ち(エラー対応)。proposal由来との内訳表示用 (2026-07-08)
    const deferred = entries.filter((e) => e.status === 'needs_review' && e.id.startsWith('deferred_')).length;
    const alreadyRegistered = entries.filter((e) => e.alreadyRegistered).length;
    const approvedUnregistered = entries.filter(
      (e) => e.status === 'approved' && !e.alreadyRegistered,
    ).length;
    const totalAmount = entries.reduce((s, e) => s + e.grandTotal, 0);
    return { total, approved, approvedUnregistered, alreadyRegistered, pending, needsReview, deferred, blocked, totalAmount };
  }, [entries]);

  const activeEntry = useMemo(
    () => (activePanelId ? entries.find((e) => e.id === activePanelId) ?? null : null),
    [entries, activePanelId]
  );

  // 支払月セレクタ options: all + [当月-2 .. 当月+2] + 現在値 (外れ値補完)
  const monthOptions = useMemo(() => {
    const now = currentYyyyMm();
    const months = new Set<string>();
    for (let i = -2; i <= 2; i += 1) months.add(monthOffset(now, i));
    if (targetPaymentMonth !== 'all') months.add(targetPaymentMonth);
    const sorted = Array.from(months).sort();
    return [{ name: '全て', value: 'all' }, ...sorted.map((m) => ({ name: m, value: m }))];
  }, [targetPaymentMonth]);

  const changeTargetPaymentMonth = useCallback((v: string) => {
    setTargetPaymentMonth(v);
    if (typeof window !== 'undefined') {
      if (v === 'all') {
        if (window.location.hash) {
          window.location.hash = '#invoice';
        }
      } else {
        window.location.hash = `#invoice/${v}`;
      }
    }
  }, []);

  // Handlers
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((e) => e.id))
    );
  }, [filtered]);

  // Resolve entry.id (synthetic `proposal_N`) → deal_proposals.ref_number (`arica:NNNN`)
  // via entries state. Used by both single-row approve (updateStatus) and bulk approveSelected.
  const refByEntryId = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of entries) {
      const ref = e.refNumber || (e.id.startsWith('proposal_') ? `arica:${e.rowid}` : null);
      if (ref) m.set(e.id, ref);
    }
    return m;
  }, [entries]);

  const updateStatus = useCallback((id: string, status: VerificationStatus) => {
    // Capture previous status for rollback on API failure.
    let previousStatus: VerificationStatus | null = null;
    setEntries((prev) => {
      const current = prev.find((e) => e.id === id);
      if (current) previousStatus = current.status;
      return prev.map((e) => (e.id === id ? { ...e, status } : e));
    });

    // Approve flow (2026-07-10 リアルタイム進捗版):
    // (1) 行に「登録キュー投入中…」チップ (スピナー) を即表示
    // (2) /api/deal-proposals/:ref/approve (auto_enroll 既定 true) → 応答の batch_id を受領
    // (3) batch item state を 5s ポーリングし「登録中→照合中→登録済み(緑)/失敗(赤)」を実況
    //     (楽観の「即・登録済み」表示は廃止 — verified 到達で初めて ✓登録済)
    // batch_id 未返却 (worker 未 deploy) の間は旧 register-via-worker chain に fallback。
    // R2 [[feedback_verification_cleanup]] 準拠: UI click が起点、 worker proxy 経由
    if (status === 'approved') {
      const ref = refByEntryId.get(id);
      if (!ref) {
        console.warn('[approve] no ref for entry', id, '— skipping');
        return;
      }
      clearRowPoll(id);
      setRowRegByEntryId((prev) => ({ ...prev, [id]: { phase: 'queueing', ref } }));
      (async () => {
        // 2026-09-08 fix: 単票承認でも enroll 前に session lease を確保する (一括承認
        // approveSelected と同じ作法に統一)。旧実装は batch_id が返る新経路で早期 return し、
        // ensureSessionLocal() は下の fallback 分岐でしか呼ばれていなかった。結果として
        // 単票承認の成否が「たまたま別の有効 session が開いているか」に依存し、
        // worker 側 drain が register_401 (no_valid_session_available) で failed 固着していた
        // (実発生 2026-09-08: arica:33306/33267/33249 計 ¥739,330)。
        // worker 側にも system session による恒久是正 (batchDrainSweep) を入れてあるが、
        // ここでも確保しておくことで「人間の操作起点」という trace が session にも残る。
        // 失敗しても続行 — worker 側 fallback / system session が受けるため承認自体は止めない。
        await ensureSessionLocal().catch(() => null);
        let approveResp: any = null;
        try {
          approveResp = await approveDealProposal(ref);
        } catch (err: any) {
          console.error('[approve] relay failed — rolling back UI:', err);
          setRowRegByEntryId((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          if (previousStatus !== null) {
            const prevCopy = previousStatus;
            setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status: prevCopy } : e)));
          }
          setBatchResultToast(`承認に失敗しました (${ref}): ${err?.message || String(err)}`);
          setTimeout(() => setBatchResultToast(null), 8000);
          return;
        }
        // worker auto_enroll → batch_id が返る (= 単票バッチ)。実 state をポーリング。
        const batchId = approveResp?.batch_id ? String(approveResp.batch_id) : null;
        if (batchId) {
          setRowRegByEntryId((prev) => ({ ...prev, [id]: { phase: 'registering', ref, batchId } }));
          pollBatchForRow(id, ref, batchId);
          return;
        }
        // ---- fallback: batch_id 未返却 (worker 未 deploy) → 旧 register-via-worker chain ----
        const sid = await ensureSessionLocal();
        if (!sid) {
          console.warn('[register-via-worker] session not available — approved only, freee POST skipped');
          setRowRegByEntryId((prev) => ({
            ...prev,
            [id]: { phase: 'failed', ref, error: 'worker session を確保できず freee 登録を開始できませんでした (承認自体は完了)。#batches から再試行するか、再度承認してください。' },
          }));
          return;
        }
        setRowRegByEntryId((prev) => ({ ...prev, [id]: { phase: 'registering', ref } }));
        try {
          const r = await registerProposalViaWorker(ref, sid);
          console.log('[register-via-worker] success', ref, 'freee_deal_id=', r?.freee_deal_id);
          setRowRegByEntryId((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          const dealId = r?.freee_deal_id ?? null;
          setEntries((prev) => prev.map((e) => (e.id === id
            ? { ...e, alreadyRegistered: true, registeredDealId: dealId ?? e.registeredDealId }
            : e)));
        } catch (err: any) {
          // freee POST 失敗 → status='approved' のまま、行チップに失敗を明示。後で retry 可能。
          console.error('[register-via-worker] freee POST failed for', ref, err);
          setRowRegByEntryId((prev) => ({
            ...prev,
            [id]: { phase: 'failed', ref, error: err?.message || String(err) },
          }));
        }
      })();
      return;
    }

    if (status === 'blocked') {
      // 2026-07-08: 旧 rejectEntry (POST /api/instructions, reject_entry_simple) は
      // ミニPC Runner に届かない不達経路だった。approve 側と対称の worker-native
      // /api/deal-proposals/:ref/reject (D1 status flip・AI不要) に切替。
      const ref = refByEntryId.get(id);
      if (!ref) {
        console.warn('[reject] no ref for entry', id, '— skipping');
        return;
      }
      rejectDealProposal(ref, undefined, 'web_app_ui').catch((err) => {
        console.error('[reject] relay failed — rolling back UI:', err);
        if (previousStatus !== null) {
          const prevCopy = previousStatus;
          setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, status: prevCopy } : e)));
        }
      });
    }
  }, [refByEntryId, clearRowPoll, pollBatchForRow]);

  // Bulk approve: throttle parallel POSTs to avoid overwhelming the Worker/D1.
  const approveSelected = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    // Resolve ref_numbers and drop any non-proposal rows (legacy static rows
    // that don't have a corresponding deal_proposals record).
    const pairs = ids
      .map((id) => ({ id, ref: refByEntryId.get(id) }))
      .filter((p) => !!p.ref) as Array<{ id: string; ref: string }>;
    const skipped = ids.length - pairs.length;
    if (skipped > 0) {
      console.warn(`[bulk-approve] skipped ${skipped} non-proposal rows (no ref_number)`);
    }
    if (pairs.length === 0) return;

    // Capture previous statuses for rollback.
    const previousById = new Map<string, VerificationStatus>();
    setEntries((prev) => {
      prev.forEach((e) => { if (selectedIds.has(e.id)) previousById.set(e.id, e.status); });
      return prev.map((e) => (selectedIds.has(e.id) ? { ...e, status: 'approved' as const } : e));
    });
    setSelectedIds(new Set());

    // Phase 0 (blueprint v2): approve 完了後、client側の register ループを廃止し
    // バッチ完全性台帳 (POST /api/batches) へ enroll。登録drain+freee独立照合は worker が実行し、
    // 進捗は #batches の「登録 n/N・照合 n/N・証憑添付 n/N」で全件追跡できる (「全部できたはず」の廃止)。
    // 2026-07-10: 承認開始〜enroll完了までフルスクリーンオーバーレイでグレーアウト+リアルタイム進捗表示。
    setBulkProgress({ phase: 'approving', done: 0, total: pairs.length, failed: 0, approvedRefs: [] });
    const CONCURRENCY = 3;
    let cursor = 0;
    const approveFailed: string[] = [];
    const approvedRefs: string[] = [];
    await ensureSessionLocal();
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, pairs.length) }).map(async () => {
        while (cursor < pairs.length) {
          const i = cursor++;
          const { id, ref } = pairs[i];
          // 一括経路は下で自前 createBatch する — worker 側 auto_enroll による単票バッチの乱立を防ぐ
          try { await approveDealProposal(ref, undefined, { auto_enroll: false }); approvedRefs.push(ref); }
          catch (err) {
            console.error('[bulk-approve] approve failed:', id, ref, err);
            approveFailed.push(id);
          }
          setBulkProgress((p) => (p ? { ...p, done: Math.min(p.done + 1, p.total), failed: approveFailed.length } : p));
        }
      })
    );

    // approve 失敗のみ UI rollback
    if (approveFailed.length > 0) {
      setEntries((prev) => prev.map((e) => {
        if (!approveFailed.includes(e.id)) return e;
        const prevStatus = previousById.get(e.id);
        return prevStatus ? { ...e, status: prevStatus } : e;
      }));
    }

    // 全件 approve 失敗 → enroll せず画面上にエラー表示 (console-only 禁止)
    if (approvedRefs.length === 0) {
      setBulkProgress({
        phase: 'error', done: pairs.length, total: pairs.length, failed: approveFailed.length, approvedRefs: [],
        errorText: `全${pairs.length}件の承認に失敗しました。該当行は元のステータスに戻しています。通信状態を確認し、再度選択して一括承認してください。`,
      });
      return;
    }

    // バッチ台帳 enroll → worker が drain(登録)+verify(照合)+証憑添付 を実行。
    // 失敗は console でなく画面上に大きく表示し [再試行] を出す (前回 console-only 事故の再発防止)。
    setBulkProgress((p) => (p
      ? { ...p, phase: 'enrolling', approvedRefs }
      : { phase: 'enrolling', done: pairs.length, total: pairs.length, failed: approveFailed.length, approvedRefs }));
    try {
      const b = await createBatch(approvedRefs);
      console.log('[bulk-approve] batch enrolled', b.batch_id, `${b.total_count}件`);
      setBulkProgress(null);
      window.location.hash = `#batches/${b.batch_id}`;
    } catch (err: any) {
      console.error('[bulk-approve] batch enroll failed (approve済):', err?.message || err);
      setBulkProgress({
        phase: 'error', done: pairs.length, total: pairs.length, failed: approveFailed.length, approvedRefs,
        errorText: `バッチ完全性台帳への登録(enroll)に失敗しました: ${err?.message || String(err)}`,
      });
    }
  }, [selectedIds, refByEntryId]);

  // enroll 失敗時の再試行 — 承認済み refs をそのまま POST /api/batches へ再送
  const retryEnroll = useCallback(async () => {
    const refs = bulkProgress?.approvedRefs || [];
    if (refs.length === 0) { setBulkProgress(null); return; }
    setBulkProgress((p) => (p ? { ...p, phase: 'enrolling', errorText: undefined } : p));
    try {
      const b = await createBatch(refs);
      console.log('[bulk-approve] batch enrolled (retry)', b.batch_id, `${b.total_count}件`);
      setBulkProgress(null);
      window.location.hash = `#batches/${b.batch_id}`;
    } catch (err: any) {
      console.error('[bulk-approve] batch enroll retry failed:', err?.message || err);
      setBulkProgress((p) => (p ? {
        ...p, phase: 'error',
        errorText: `バッチ完全性台帳への登録(enroll)に再度失敗しました: ${err?.message || String(err)}`,
      } : p));
    }
  }, [bulkProgress]);

  const handleRowClick = useCallback(
    (entry: JournalEntry) => {
      // Toggle side panel
      if (activePanelId === entry.id) {
        setActivePanelId(null);
      } else {
        setActivePanelId(entry.id);
        setSidePanelTab(0);
      }
    },
    [activePanelId]
  );

  const handleExpandToggle = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setExpandedId((prev) => (prev === id ? null : id));
    },
    []
  );

  const openCorrection = useCallback((id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCorrectionEntryId(id);
  }, []);

  const closeCorrection = useCallback(() => {
    setCorrectionEntryId(null);
  }, []);

  const pushToast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { ...t, id }]);
    // Auto-remove after 6s
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 6000);
  }, []);

  const handleCorrectionSuccess = useCallback(
    (instructionId: string, action: CorrectionAction) => {
      const entryId = correctionEntryId;
      if (entryId) {
        setSentInstructions((prev) => ({
          ...prev,
          [entryId]: { instructionId, action, at: new Date().toISOString() },
        }));
        // Reflect rejection locally so the row badge updates right away.
        if (action === 'reject') {
          setEntries((prev) =>
            prev.map((e) => (e.id === entryId ? { ...e, status: 'blocked' as const } : e)),
          );
        }
      }
      const label =
        action === 'correct' ? '修正指示' :
        action === 'reject'  ? '却下指示' :
                               '再処理依頼';
      pushToast({
        kind: 'success',
        text: `${label}を送信しました (ID: ${instructionId})`,
      });
      setCorrectionEntryId(null);
    },
    [correctionEntryId, pushToast],
  );

  // ---- Deferred row actions (2026-05-13) -----------------------------------
  // 主 list 内に 1 行表示された deferred entry に対する actions。
  // 旧 DeferredQueueTab 内 DetailPanel と同 API (resolveDeferredInvoice / requeueDeferredInvoice) を call。
  const handleDeferredRequeue = useCallback(async (entryId: string) => {
    const def = deferredByEntryId[entryId];
    if (!def) {
      pushToast({ kind: 'error', text: '再キュー化対象が見つかりません' });
      return;
    }
    try {
      const r = await requeueDeferredInvoice(def.id, {
        partner_id: def.partner_id ?? undefined,
        total_amount: def.total_amount ?? undefined,
      });
      pushToast({
        kind: 'success',
        text: `再キュー化 instruction emit: ${r.instruction_id}`,
      });
      // 楽観的 UI: 行を消す (= 次回 poll で正式反映)
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      setActivePanelId(null);
    } catch (e: any) {
      pushToast({ kind: 'error', text: `再キュー化失敗: ${e.message || e}` });
    }
  }, [deferredByEntryId, pushToast]);

  const handleDeferredResolve = useCallback(async (entryId: string) => {
    const def = deferredByEntryId[entryId];
    if (!def) {
      pushToast({ kind: 'error', text: '解決対象が見つかりません' });
      return;
    }
    try {
      await resolveDeferredInvoice(def.id, {
        resolution: 'manual_resolved',
        partner_id: def.partner_id ?? undefined,
        total_amount: def.total_amount ?? undefined,
      });
      pushToast({ kind: 'success', text: '解決済として閉じました。' });
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
      setActivePanelId(null);
    } catch (e: any) {
      pushToast({ kind: 'error', text: `解決失敗: ${e.message || e}` });
    }
  }, [deferredByEntryId, pushToast]);

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderKpiCard = (
    label: string,
    value: string,
    accentColor: string,
    subtext?: string,
    onClick?: () => void,
    active?: boolean,
  ) => (
    <div
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        outline: active ? `2px solid ${accentColor}` : 'none',
        outlineOffset: active ? '-2px' : undefined,
        borderRadius: '6px',
        transition: 'outline 0.15s ease, transform 0.1s ease',
      }}
    >
      <CardBase paddingSize="small" overflowHidden={false}>
        <div style={S.kpiCard}>
          <div style={S.kpiAccent(accentColor)} />
          <div style={{ paddingLeft: '8px' }}>
            <div style={{ fontSize: '0.6875rem', color: '#8a8580', marginBottom: '4px', fontWeight: 500 }}>
              {label}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#252525', lineHeight: 1.1 }}>
              {value}
            </div>
            {subtext && (
              <div style={{ fontSize: '0.625rem', color: '#8a8580', marginTop: '4px' }}>{subtext}</div>
            )}
          </div>
        </div>
      </CardBase>
    </div>
  );

  const renderConfidence = (c: number) => {
    const pct = Math.round(c * 100);
    const color = confidenceColor(c);
    return (
      <div style={{ minWidth: '48px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color, textAlign: 'center' }}>
          {pct}%
        </div>
        <div style={S.confidenceTrack}>
          <div style={S.confidenceFill(pct, confidenceBarColor(c))} />
        </div>
      </div>
    );
  };

  const renderPatternBadge = (status: string) => {
    const cfg = PATTERN_STATUS_LABELS[status] ?? PATTERN_STATUS_LABELS.Unknown;
    return <span style={S.badge(cfg.bg, cfg.color)}>{cfg.label}</span>;
  };

  // 単体行承認のリアルタイム進捗チップ (2026-07-10)。
  //   登録キュー投入中/登録中/照合中 = スピナー付き。失敗 = 赤。
  //   クリックで #batches (バッチ完全性台帳) へ。title に「途中でページを離れても追える」旨を明記。
  const renderRowRegChip = (reg: RowRegProgress) => {
    const cfg = ROW_REG_CHIP[reg.phase];
    const href = reg.batchId ? `#batches/${reg.batchId}` : '#batches';
    const title = reg.phase === 'failed'
      ? `freee 登録に失敗しました${reg.error ? `: ${reg.error}` : ''} — クリックでバッチ完全性台帳 (${href}) を開き、詳細確認・再試行できます`
      : `freee への登録・照合は途中でこのページを離れてもバッチ完全性台帳 (${href}) でリアルタイムに追えます — クリックで開く`;
    return (
      <a
        href={href}
        onClick={(e) => e.stopPropagation()}
        title={title}
        style={{
          ...S.badge(cfg.bg, cfg.color),
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          textDecoration: 'none',
          cursor: 'pointer',
        }}
      >
        {cfg.spin && (
          <span style={{ display: 'inline-flex', transform: 'scale(0.7)', transformOrigin: 'center' }}>
            <InlineSpinner isLoading />
          </span>
        )}
        {cfg.label}
      </a>
    );
  };

  const renderDetailTable = (entry: JournalEntry) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={S.detailTh}>借方科目</th>
          <th style={{ ...S.detailTh, textAlign: 'right' }}>金額</th>
          <th style={{ ...S.detailTh, textAlign: 'right' }}>税額</th>
          <th style={S.detailTh}>税区分</th>
          <th style={S.detailTh}>部門</th>
          <th style={S.detailTh}>品目</th>
          <th style={S.detailTh}>摘要</th>
          <th style={S.detailTh}>学習根拠</th>
        </tr>
      </thead>
      <tbody>
        {entry.details.map((d: JournalDetail) => (
          <tr key={d.index}>
            <td style={{ ...S.detailTd, fontWeight: 600, color: '#252525' }}>{resolveAccountLabel(accountItemsMap, d.accountName, d.accountItemId)}</td>
            <td style={{ ...S.detailTd, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(d.amount)}
            </td>
            <td style={{ ...S.detailTd, textAlign: 'right', color: '#8a8580', fontVariantNumeric: 'tabular-nums' }}>
              {fmt(d.taxAmount)}
            </td>
            <td style={S.detailTd}>
              <span style={S.badge('#f5f3f2', '#6b6560')}>{d.taxCodeName}</span>
            </td>
            <td style={S.detailTd}>
              {(!d.sectionId || d.sectionId === 0) ? (
                <a
                  href="#section_unknown"
                  style={{ ...S.badge('#fff7ed', '#d97706'), textDecoration: 'none', cursor: 'pointer' }}
                  title="部門未確定 — 部門不明一覧 で 確定してください"
                >
                  部門不明
                </a>
              ) : (
                <span style={{ color: '#252525', fontWeight: 500 }}>{d.sectionName}</span>
              )}
            </td>
            <td style={S.detailTd}>
              {d.itemName ? (
                <span style={{ color: '#252525' }}>{d.itemName}</span>
              ) : (
                <span style={{ color: '#c4c0bc' }}>--</span>
              )}
            </td>
            <td style={{ ...S.detailTd, maxWidth: '180px' }}>
              <span style={{ color: '#6b6560', fontSize: '0.6875rem' }}>{d.description}</span>
            </td>
            <td style={S.detailTd}>
              {d.mappingSource ? (
                <span style={S.badge('#e6f4ea', '#338650')}>{d.mappingSource}</span>
              ) : (
                <span style={{ color: '#c4c0bc', fontSize: '0.6875rem' }}>未設定</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
      {/* Tax breakdown summary row */}
      <tfoot>
        <tr>
          <td
            colSpan={8}
            style={{
              padding: '8px 10px',
              background: '#f7f5f5',
              fontSize: '0.6875rem',
              color: '#6b6560',
            }}
          >
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, color: '#252525' }}>税額内訳:</span>
              {entry.taxBreakdown.map((tb, i) => (
                <span key={i}>
                  {tb.rate} : 税抜 {fmt(tb.subtotal)} / 税額 {fmt(tb.tax)}
                </span>
              ))}
              <span style={{ marginLeft: 'auto', fontWeight: 600, color: '#252525' }}>
                合計: {fmt(entry.grandTotal)}
              </span>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  );

  const renderSidePanel = () => {
    if (!activeEntry) return null;
    return (
      <div style={{ minHeight: '1px' }}>
        <div style={S.sidePanel}>
          {/* Panel header */}
          <div style={S.sidePanelHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text weight="bold" size={0.875}>
                  {activeEntry.partnerName}
                </Text>
                <div style={{ marginTop: '4px' }}>
                  <Text size={0.75} color="burnt">
                    #{activeEntry.rowid}
                  </Text>
                  <span style={{ margin: '0 6px', color: '#dbd9d7' }}>|</span>
                  {rowRegByEntryId[activeEntry.id] ? (
                    renderRowRegChip(rowRegByEntryId[activeEntry.id])
                  ) : (
                    <StatusIcon type={statusToIcon(activeEntry.status)}>
                      {statusToLabel(activeEntry.status, activeEntry.id, !!sentInstructions[activeEntry.id])}
                    </StatusIcon>
                  )}
                </div>
              </div>
              <Button
                small
                appearance="tertiary"
                onClick={() => setActivePanelId(null)}
              >
                閉じる
              </Button>
            </div>
            <div style={{ marginTop: '8px' }}>
              <TabBar
                tabs={['証憑', 'メタデータ']}
                currentTabIndex={sidePanelTab}
                onClickTab={(i) => setSidePanelTab(i)}
                small
              />
            </div>
          </div>

          {/* Panel body */}
          <div style={S.sidePanelBody}>
            {sidePanelTab === 0 && (
              <div>
                {/* PDF Viewer — show all attached receipts as tabs when a proposal has multiple. */}
                <div style={{ borderBottom: '1px solid #dbd9d7' }}>
                  {(() => {
                    const proposal = proposalByEntryId[activeEntry.id];
                    const receipts = receiptsForProposal(proposal);
                    // Fallback to the ARICA pdf URL when there's no attached receipts list yet
                    // (e.g., batch1 static rows that predate the proposal system).
                    if (receipts.length === 0) {
                      // Guard: if rowid is missing/invalid, skip rendering to avoid /pdf/undefined 400.
                      if (!activeEntry.rowid || activeEntry.rowid <= 0) {
                        return (
                          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#8a8580', fontSize: '0.875rem' }}>
                            証憑なし
                          </div>
                        );
                      }
                      return (
                        <PdfViewer
                          url={`https://invoice-review.system-0f7.workers.dev/pdf/${activeEntry.rowid}`}
                          title={activeEntry.partnerName}
                          height="560px"
                        />
                      );
                    }
                    return (
                      <PdfViewer
                        files={receipts}
                        title={activeEntry.partnerName}
                        height="560px"
                      />
                    );
                  })()}
                </div>
                {/* Quick actions */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid #edebea' }}>
                  {deferredByEntryId[activeEntry.id] ? (
                    // 2026-05-11 Team V: deferred entry の場合は requeue / resolve action を表示
                    //   (= UI 上では普通の row だが、 drill-down 内のみ defer 機能を維持)
                    <VStack gap={0.5}>
                      <Note>
                        エラー対応の理由: {activeEntry.flags[0] || '不明'}。 取引先 / 金額 を 修正モーダルで補完してから 再キュー化してください。
                        取込中のエラーは <a href="#invoice_runs">取込run台帳</a> から個別に対応できます。
                      </Note>
                      <HStack gap={0.5}>
                        <Button
                          appearance="primary"
                          small
                          onClick={() => handleDeferredRequeue(activeEntry.id)}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <MdLoop /> 再キュー化
                          </span>
                        </Button>
                        <Button
                          appearance="secondary"
                          small
                          onClick={() => handleDeferredResolve(activeEntry.id)}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <MdCheckCircle /> 解決済として閉じる
                          </span>
                        </Button>
                        <Button
                          appearance="tertiary"
                          small
                          href={`https://invoice-review.system-0f7.workers.dev/pdf/${activeEntry.rowid}`}
                          target="_blank"
                        >
                          別タブで開く
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <HStack gap={0.5}>
                      <Button
                        appearance="primary"
                        small
                        onClick={() => updateStatus(activeEntry.id, 'approved')}
                        disabled={activeEntry.status === 'approved'}
                      >
                        承認する
                      </Button>
                      <Button
                        appearance="secondary"
                        small
                        danger
                        onClick={() => updateStatus(activeEntry.id, 'blocked')}
                        disabled={activeEntry.status === 'blocked'}
                      >
                        却下する
                      </Button>
                      <Button
                        appearance="tertiary"
                        small
                        href={`https://invoice-review.system-0f7.workers.dev/pdf/${activeEntry.rowid}`}
                        target="_blank"
                      >
                        別タブで開く
                      </Button>
                    </HStack>
                  )}
                  {/* freee登録済ならdeal詳細ページへのリンクを表示 */}
                  {activeEntry.alreadyRegistered && activeEntry.registeredDealId && (
                    <div style={{ marginTop: '8px', fontSize: '0.75rem' }}>
                      <a
                        href={`https://secure.freee.co.jp/deals#deal_id=${activeEntry.registeredDealId}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          background: '#e6f4ea',
                          color: '#1e6a36',
                          borderRadius: '10px',
                          textDecoration: 'none',
                          fontWeight: 600,
                        }}
                      >
                        ✓ freeeで仕訳を開く (deal #{activeEntry.registeredDealId}) ↗
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {sidePanelTab === 1 && (
              <div style={{ padding: '16px 20px' }}>
                <VStack gap={0.25}>
                  <SubSectionTitle headlineLevel={4}>請求書情報</SubSectionTitle>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>取引先</span>
                    <span style={S.metaValue}>{activeEntry.partnerName}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>T番号</span>
                    <span style={S.metaValue}>{activeEntry.tNumber ?? '--'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>請求書番号</span>
                    <span style={S.metaValue}>{activeEntry.invoiceNumber ?? '--'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>証憑タイプ</span>
                    <span style={S.metaValue}>{activeEntry.verifiedType}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>発行日</span>
                    <span style={S.metaValue}>{activeEntry.issueDate}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>支払期日</span>
                    <span style={S.metaValue}>{activeEntry.dueDate}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>合計金額</span>
                    <span style={{ ...S.metaValue, fontWeight: 700, fontSize: '0.875rem' }}>{fmt(activeEntry.grandTotal)}</span>
                  </div>
                </VStack>

                <div style={{ marginTop: '16px' }}>
                  <SubSectionTitle headlineLevel={4}>パターン学習</SubSectionTitle>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>パターンステータス</span>
                    <span>{renderPatternBadge(activeEntry.patternStatus)}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>信頼度</span>
                    <span style={{ ...S.metaValue, color: confidenceColor(activeEntry.confidence), fontWeight: 700 }}>
                      {Math.round(activeEntry.confidence * 100)}%
                    </span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>A2A合意</span>
                    <span style={S.metaValue}>{activeEntry.a2aAgreement ? 'Yes' : 'No'}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>対話ラウンド</span>
                    <span style={S.metaValue}>{activeEntry.dialogueRounds}</span>
                  </div>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <SubSectionTitle headlineLevel={4}>税額内訳</SubSectionTitle>
                  {activeEntry.taxBreakdown.map((tb, i) => (
                    <div key={i} style={S.metaRow}>
                      <span style={S.metaLabel}>{tb.rate}</span>
                      <span style={S.metaValue}>
                        {fmt(tb.subtotal)} + {fmt(tb.tax)}
                      </span>
                    </div>
                  ))}
                </div>

                {activeEntry.notes && (
                  <div style={{ marginTop: '16px' }}>
                    <SubSectionTitle headlineLevel={4}>備考</SubSectionTitle>
                    <div
                      style={{
                        marginTop: '4px',
                        padding: '10px 12px',
                        background: '#f7f5f5',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: '#6b6560',
                        lineHeight: 1.5,
                      }}
                    >
                      {activeEntry.notes}
                    </div>
                  </div>
                )}

                {/* Flags */}
                {activeEntry.flags.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <SubSectionTitle headlineLevel={4}>フラグ</SubSectionTitle>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                      {activeEntry.flags.map((f, i) => (
                        <span key={i} style={S.badge('#fff3e0', '#c67320')}>
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  const approvedPct = stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0;

  return (
    <div style={S.page}>
      <Container width="wide">
        {/* Breadcrumbs */}
        <div style={{ padding: '16px 0 8px' }}>
          <Breadcrumbs
            links={[
              { title: 'FREEEMAN', url: '/' },
              { title: '請求書処理', url: '#invoice' },
              ...(pageSubTab === 'coverage'
                ? [{ title: '受信一覧', url: '#invoice/coverage' }]
                : [targetPaymentMonth === 'all'
                    ? { title: `バッチ${batch1Summary.batchNumber}`, url: `#invoice` }
                    : { title: `支払月 ${targetPaymentMonth}`, url: `#invoice/${targetPaymentMonth}` }]),
            ]}
          />
        </div>

        {/* Page-level sub tabs (2026-05-13) */}
        <CardBase paddingSize="small" mb={0.5}>
          <TabBar
            tabs={SUB_TAB_LABELS}
            currentTabIndex={SUB_TABS.indexOf(pageSubTab)}
            onClickTab={onChangeSubTab}
          />
        </CardBase>

        {pageSubTab === 'coverage' && (
          <InvoiceCoverageTab />
        )}

        {pageSubTab === 'bank_auto_debit' && (
          <BankAutoDebitInvoicePage />
        )}

        {pageSubTab === 'verify' && (<>
        {/* Page Title */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <HStack gap={1} alignItems="center">
              <PageTitle>
                仕訳検証
              </PageTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={S.badge('#e8f0fe', '#2e7cf6')}>
                  {targetPaymentMonth === 'all' ? batch1Summary.targetMonth : targetPaymentMonth}
                </span>
                <span style={S.badge('#f5f3f2', '#6b6560')}>
                  25日払い
                </span>
                <a href="#invoice_runs" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#2e7cf6', whiteSpace: 'nowrap' }}>
                  取込run台帳へ ↗
                </a>
              </div>
            </HStack>
            <HStack gap={0.5}>
              <SelectBox
                options={monthOptions}
                value={targetPaymentMonth}
                onChange={(e) => changeTargetPaymentMonth(e.target.value)}
                width="small"
                small
                label="支払月"
              />
              <SelectBox
                options={[
                  { name: '支払期日 昇順', value: 'dueDate_asc' },
                  { name: '支払期日 降順', value: 'dueDate_desc' },
                  { name: '発生日 昇順', value: 'issueDate_asc' },
                  { name: '発生日 降順', value: 'issueDate_desc' },
                  { name: '金額 降順', value: 'amount_desc' },
                  { name: '金額 昇順', value: 'amount_asc' },
                  { name: '取引先', value: 'partnerName_asc' },
                ]}
                value={`${sortKey}_${sortDir}`}
                onChange={(e) => {
                  const [k, d] = e.target.value.split('_') as ['dueDate' | 'issueDate' | 'amount' | 'partnerName', 'asc' | 'desc'];
                  setSortKey(k);
                  setSortDir(d);
                }}
                width="small"
                small
                label="並び順"
              />
              <Button
                appearance="primary"
                onClick={() => setBatchRegisterOpen(true)}
                disabled={stats.approvedUnregistered === 0 || batchRegistering}
              >
                {batchRegistering
                  ? '登録処理中...'
                  : `承認済を一括登録 (${stats.approvedUnregistered}件${stats.alreadyRegistered > 0 ? ` / 既登録${stats.alreadyRegistered}件はスキップ` : ''})`}
              </Button>
              <Button
                appearance="secondary"
                disabled={batchRegistering}
                onClick={async () => {
                  if (!window.confirm('登録済 deal に PDF 証憑を 一括 紐付けます (= backfill)。 freee_receipt_id 未設定の RID 全件 対象。 続行しますか？')) return;
                  setBatchRegistering(true);
                  try {
                    // session 取得失敗 (= audit blocking で /session/open が 拒否) でも、
                    // backfill endpoint 側で 既存 valid session を 自動 fallback するため null OK。
                    const sid = (await ensureSessionLocal().catch(() => null)) || '';
                    setBatchResultToast('PDF backfill 開始 — 完了まで数分かかります');
                    setActivityOpen(true);
                    const resp: any = await backfillReceipts(sid, 'all');
                    const failedSample = (resp.results?.failed || []).slice(0, 3).map((f: any) => `${f.ref}(${f.stage})`).join(', ');
                    setBatchResultToast(
                      `PDF backfill 完了: 成功${resp.success_count}件 / skip${resp.skipped_count}件 / 失敗${resp.failed_count}件${resp.failed_count > 0 ? ` (${failedSample}${resp.failed_count > 3 ? '...' : ''})` : ''}`,
                    );
                    setTimeout(() => setBatchResultToast(null), 15000);
                  } catch (err: any) {
                    setBatchResultToast(`backfill 失敗: ${err.message}`);
                    setTimeout(() => setBatchResultToast(null), 8000);
                  } finally {
                    setBatchRegistering(false);
                  }
                }}
              >
                証憑PDF 一括 backfill
              </Button>
              <Button
                appearance={proposalsPanelOpen ? 'primary' : 'secondary'}
                onClick={() => setProposalsPanelOpen((v) => !v)}
              >
                仕訳承認{pendingProposalCount > 0 ? ` (${pendingProposalCount})` : ''}
              </Button>
              <Button
                appearance={receiptPanelOpen ? 'primary' : 'secondary'}
                onClick={() => setReceiptPanelOpen((v) => !v)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <MdAssignment /> 証憑追跡
                </span>
              </Button>
              <Button
                appearance={cadencePanelOpen ? 'primary' : 'secondary'}
                onClick={() => setCadencePanelOpen((v) => !v)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <MdEventNote /> 業者cadence
                </span>
              </Button>
              <Button
                appearance={activityOpen ? 'primary' : 'secondary'}
                onClick={() => setActivityOpen((v) => !v)}
              >
                {activityOpen ? 'Activity 閉じる' : 'Activity 開く'}
              </Button>
            </HStack>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Note>
              {targetPaymentMonth === 'all'
                ? `期日 ${batch1Summary.dueDate} / 取引日 ${batch1Summary.issueDate} / ${stats.total}件の仕訳を検証`
                : `支払月 ${targetPaymentMonth} / ${stats.total}件の仕訳を検証`}
            </Note>
          </div>
        </div>

        {/* 2026-06-16: 「更新あり」通知バナー。 ポーリングで差分検知時のみ表示。
            ユーザーが押すまでビューは静止し、 押下で初めて最新データへ差し替える。
            position:fixed の浮遊ピル = 文書フローに影響せず scroll を一切動かさない。 */}
        {proposalUpdateCount > 0 && pendingProposalLookup && (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '10px 16px',
              background: '#e8f0fe',
              border: '1px solid #c4dafc',
              borderRadius: '999px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#1f5fc4', fontSize: '0.8125rem', fontWeight: 600 }}>
              <MdLoop /> 新しい更新があります ({proposalUpdateCount}件)
            </span>
            <Button appearance="primary" small onClick={flushPendingProposalUpdate}>
              表示を更新
            </Button>
          </div>
        )}

        {/* KPI Cards (2026-05-11 Team V: 5 列、 "未処理 (Deferred)" カード削除)。
            deferred items は 主 list に汎用 needs_review として透過 merge 済み。 */}
        <div style={S.kpiGrid}>
          {renderKpiCard('登録対象', `${stats.total}件`, '#2e7cf6', `${filtered.length}件表示中`)}
          {renderKpiCard('合計金額', fmt(stats.totalAmount), '#252525')}
          {renderKpiCard('完了(承認済)', `${stats.approved}件`, '#338650', `${approvedPct}%完了`)}
          {renderKpiCard('あなたの対応待ち', `${stats.needsReview}件`, '#c67320',
            stats.needsReview > 0 ? `承認 ${stats.needsReview - stats.deferred}・エラー対応 ${stats.deferred}` : undefined)}
          {renderKpiCard('除外(却下)', `${stats.blocked}件`, '#c33939')}
        </div>

        {/* Progress bar */}
        {stats.total > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <ProgressBar value={stats.approved} maxValue={stats.total} width="full" ariaLabel="承認進捗" />
              </div>
              <Text size={0.75} color="burnt">
                {stats.approved}/{stats.total} 承認済
              </Text>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        <CardBase paddingSize="zero" mb={0.5}>
          <div style={S.filterBar}>
            <SearchField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="取引先名・T番号・請求書番号で検索"
              width="large"
              small
            />
            <SelectBox
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              width="small"
              small
              label="ステータス"
            />
            <SelectBox
              options={DEPT_OPTIONS}
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              width="medium"
              small
              label="部門"
            />
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Note>{filtered.length}件</Note>
            </div>
          </div>
        </CardBase>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <MessageBlock success mb={0.5}>
            <HStack gap={1} alignItems="center">
              <Text weight="bold" size={0.875}>
                {selectedIds.size}件選択中
              </Text>
              <Button appearance="primary" small onClick={approveSelected}>
                一括承認
              </Button>
              <Button
                appearance="secondary"
                small
                danger
                onClick={() => {
                  setEntries((prev) =>
                    prev.map((e) =>
                      selectedIds.has(e.id) ? { ...e, status: 'blocked' as const } : e
                    )
                  );
                  setSelectedIds(new Set());
                }}
              >
                一括却下
              </Button>
              <Button appearance="tertiary" small onClick={() => setSelectedIds(new Set())}>
                選択解除
              </Button>
            </HStack>
          </MessageBlock>
        )}

        {/* Main two-column layout */}
        <div style={activePanelId ? S.mainLayoutWithPanel : S.mainLayout}>
          {/* LEFT: Table */}
          <CardBase paddingSize="zero" overflowHidden={false}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
                <thead>
                  <tr>
                    <th style={{ ...S.thCell, width: '36px', textAlign: 'center' }}>
                      <CheckBox
                        checked={selectedIds.size === filtered.length && filtered.length > 0}
                        onChange={toggleSelectAll}
                        small
                      />
                    </th>
                    <th style={{ ...S.thCell, width: '32px', textAlign: 'center' }}>#</th>
                    <th style={S.thCell}>取引先</th>
                    <th style={{ ...S.thCell, textAlign: 'right' }}>金額</th>
                    <th style={S.thCell}>部門</th>
                    <th style={S.thCell}>科目</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '64px' }}>信頼度</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '80px' }}>パターン</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '80px' }}>ステータス</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '80px' }}>アクション</th>
                    <th style={{ ...S.thCell, textAlign: 'center', width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((entry, idx) => (
                    <React.Fragment key={entry.id}>
                      {/* Main row */}
                      <tr
                        id={`entry-row-${entry.id}`}
                        style={{
                          ...S.row(
                            selectedIds.has(entry.id),
                            activePanelId === entry.id,
                          ),
                          ...(highlightEntryId === entry.id ? { background: '#fff3d6', transition: 'background .5s' } : {}),
                        }}
                        onClick={() => handleRowClick(entry)}
                      >
                        <td
                          style={{ ...S.tdCell, textAlign: 'center', width: '36px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CheckBox
                            checked={selectedIds.has(entry.id)}
                            onChange={() => toggleSelect(entry.id)}
                            small
                          />
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center', width: '32px' }}>
                          <span style={{ fontSize: '0.6875rem', color: '#8a8580', fontWeight: 500 }}>
                            {idx + 1}
                          </span>
                        </td>
                        <td style={S.tdCell}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontWeight: 600, color: '#252525', fontSize: '0.8125rem' }}>
                              {entry.partnerName}
                            </span>
                          </div>
                          {entry.flags.length > 0 && (
                            <div style={{ marginTop: '2px' }}>
                              {entry.flags.map((f, i) => (
                                <span key={i} style={S.flagBadge}>{f}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            ...S.tdCell,
                            textAlign: 'right',
                            fontWeight: 700,
                            fontSize: '0.8125rem',
                            fontVariantNumeric: 'tabular-nums',
                            color: '#252525',
                          }}
                        >
                          {fmt(entry.grandTotal)}
                        </td>
                        <td style={S.tdCell}>
                          <span style={{ fontSize: '0.75rem', color: '#6b6560' }}>
                            {entry.storeName}
                          </span>
                        </td>
                        <td style={S.tdCell}>
                          <div>
                            <span style={{ fontSize: '0.75rem', color: '#252525', fontWeight: 500 }}>
                              {resolveAccountLabel(accountItemsMap, entry.details[0]?.accountName, entry.details[0]?.accountItemId)}
                            </span>
                          </div>
                          {entry.details.length > 1 && (
                            <span
                              style={{
                                fontSize: '0.625rem',
                                color: '#8a8580',
                                background: '#f5f3f2',
                                padding: '0 5px',
                                borderRadius: '8px',
                              }}
                            >
                              +{entry.details.length - 1}行
                            </span>
                          )}
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center' }}>
                          {renderConfidence(entry.confidence)}
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center' }}>
                          {renderPatternBadge(entry.patternStatus)}
                        </td>
                        <td style={{ ...S.tdCell, textAlign: 'center' }}>
                          <VStack gap={0.25} alignItems="center">
                            {/* 単体承認中はリアルタイム進捗チップが status 表示を置換 (2026-07-10)。
                                楽観の「即・完了」表示を廃止し batch item の実 state に追随する。 */}
                            {rowRegByEntryId[entry.id] ? (
                              renderRowRegChip(rowRegByEntryId[entry.id])
                            ) : (
                              /* 修正指示送信済は統一バッジ「処理中(指示対応中)」に吸収 (2026-07-08) */
                              <StatusIcon type={sentInstructions[entry.id] ? 'progress' : statusToIcon(entry.status)}>
                                {statusToLabel(entry.status, entry.id, !!sentInstructions[entry.id])}
                              </StatusIcon>
                            )}
                            {entry.alreadyRegistered && (
                              entry.registeredDealId ? (
                                <a
                                  href={`https://secure.freee.co.jp/deals#deal_id=${entry.registeredDealId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  title={`freeeでdeal#${entry.registeredDealId}を開く`}
                                  style={{
                                    ...S.badge('#e8f5e9', '#2e7d32'),
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                  }}
                                >
                                  ✓ 登録済 ↗
                                </a>
                              ) : (
                                <span style={S.badge('#e8f5e9', '#2e7d32')}>✓ 登録済</span>
                              )
                            )}
                          </VStack>
                        </td>
                        <td
                          style={{ ...S.tdCell, textAlign: 'center', width: '80px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            small
                            appearance="secondary"
                            onClick={(e) => openCorrection(entry.id, e)}
                          >
                            修正
                          </Button>
                        </td>
                        <td
                          style={{ ...S.tdCell, textAlign: 'center', width: '40px' }}
                          onClick={(e) => handleExpandToggle(entry.id, e)}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              fontSize: '0.75rem',
                              color: '#8a8580',
                              transform: expandedId === entry.id ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease',
                              cursor: 'pointer',
                              userSelect: 'none',
                            }}
                          >
                            &#9660;
                          </span>
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {expandedId === entry.id && (
                        <tr>
                          <td colSpan={11} style={{ padding: 0 }}>
                            <div style={S.expandSection}>
                              {/* Detail header with section title and inline actions */}
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '10px',
                                }}
                              >
                                <SectionTitle headlineLevel={4}>
                                  仕訳明細 ({entry.details.length}行)
                                </SectionTitle>
                                <HStack gap={0.5}>
                                  <Button
                                    appearance="primary"
                                    small
                                    onClick={() => updateStatus(entry.id, 'approved')}
                                    disabled={entry.status === 'approved'}
                                  >
                                    承認
                                  </Button>
                                  <Button
                                    appearance="secondary"
                                    small
                                    onClick={() => openCorrection(entry.id)}
                                  >
                                    修正
                                  </Button>
                                  <Button
                                    appearance="secondary"
                                    small
                                    danger
                                    onClick={() => updateStatus(entry.id, 'blocked')}
                                    disabled={entry.status === 'blocked'}
                                  >
                                    却下
                                  </Button>
                                </HStack>
                              </div>

                              {/* Registration dates summary */}
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '16px',
                                  padding: '8px 12px',
                                  marginBottom: '6px',
                                  background: '#fafafa',
                                  border: '1px solid #e5e4e2',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#6b6560',
                                }}
                              >
                                <span>
                                  <b style={{ color: '#8a8580', fontWeight: 500 }}>発生日（登録予定）:</b>{' '}
                                  <span style={{ color: '#252525', fontWeight: 600 }}>
                                    {entry.issueDate || '—'}
                                  </span>
                                </span>
                                <span>
                                  <b style={{ color: '#8a8580', fontWeight: 500 }}>支払期日:</b>{' '}
                                  <span style={{ color: '#252525', fontWeight: 600 }}>
                                    {entry.dueDate || '—'}
                                  </span>
                                </span>
                                {entry.invoiceNumber && (
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>請求書番号:</b>{' '}
                                    <span style={{ color: '#252525', fontFamily: 'monospace' }}>
                                      {entry.invoiceNumber}
                                    </span>
                                  </span>
                                )}
                                {entry.tNumber && (
                                  <span>
                                    <b style={{ color: '#8a8580', fontWeight: 500 }}>T番号:</b>{' '}
                                    <span style={{ color: '#252525', fontFamily: 'monospace' }}>
                                      {entry.tNumber}
                                    </span>
                                  </span>
                                )}
                              </div>

                              {/* Detail table */}
                              <ColumnBase paddingSize="zero" border="default" rounded>
                                {renderDetailTable(entry)}
                              </ColumnBase>

                              {/* Notes */}
                              {entry.notes && (
                                <div
                                  style={{
                                    marginTop: '10px',
                                    padding: '8px 12px',
                                    background: '#fff',
                                    border: '1px solid #edebea',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    color: '#6b6560',
                                  }}
                                >
                                  <span style={{ fontWeight: 600, color: '#8a8580', marginRight: '8px' }}>
                                    備考:
                                  </span>
                                  {entry.notes}
                                </div>
                              )}

                              {/* Inline PDF preview (only shown when side panel is closed) */}
                              {!activePanelId && (
                                <div style={{ marginTop: '12px' }}>
                                  <PdfViewer
                                    url={`https://invoice-review.system-0f7.workers.dev/pdf/${entry.rowid}`}
                                    title={entry.partnerName}
                                    height="450px"
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ padding: '48px 24px', textAlign: 'center' }}>
                        <VStack gap={0.5} alignItems="center">
                          <Text size={0.875} color="burnt">
                            条件に一致する仕訳がありません
                          </Text>
                          <Note>フィルターを変更してください</Note>
                        </VStack>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardBase>

          {/* RIGHT: Side panel */}
          {activePanelId && renderSidePanel()}
        </div>

        {/* Footer summary */}
        <div style={S.summaryFooter}>
          <HStack gap={0.5} alignItems="center">
            {selectedIds.size > 0 && (
              <Button appearance="primary" onClick={approveSelected}>
                選択した{selectedIds.size}件を一括承認
              </Button>
            )}
          </HStack>
          <Note>
            {targetPaymentMonth === 'all'
              ? `${batch1Summary.targetMonth} | 期日 ${batch1Summary.dueDate} | ${stats.approved}/${stats.total} 承認済`
              : `支払月 ${targetPaymentMonth} | ${stats.approved}/${stats.total} 承認済`}
          </Note>
        </div>
        </>)}
      </Container>

      {/* Correction modal */}
      <CorrectionModal
        isOpen={correctionEntryId !== null}
        entry={correctionEntryId ? entries.find((e) => e.id === correctionEntryId) ?? null : null}
        deptOptions={DEPT_OPTIONS}
        onClose={closeCorrection}
        onSuccess={handleCorrectionSuccess}
      />

      {/* Claude Activity side drawer */}
      <ActivityPanel
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
      />

      {/* Deal proposals — user approval gate (MASTER_PIPELINE_RULES §0.1.1) */}
      <DealProposalsPanel
        open={proposalsPanelOpen}
        onClose={() => setProposalsPanelOpen(false)}
        onApprovedCountChange={setPendingProposalCount}
      />

      {/* 証憑追跡 (schema-v18 / 2026-04-22) — 全 ARICA receipts 列挙 + 処理状態可視化 + 復元 */}
      <ReceiptTrackingPanel
        open={receiptPanelOpen}
        onClose={() => setReceiptPanelOpen(false)}
      />

      {/* 業者 cadence (schema-v18) — 月末/20日発生 等の自動学習済みパターン可視化 */}
      <PartnerCadencePanel
        open={cadencePanelOpen}
        onClose={() => setCadencePanelOpen(false)}
      />

      {/* Batch register confirm dialog */}
      {batchRegisterOpen && (
        <div
          onClick={() => !batchRegistering && setBatchRegisterOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1500,
            background: 'rgba(37,37,37,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, padding: 24, width: 480, maxWidth: '90vw',
              boxShadow: '0 8px 32px rgba(0,0,0,.3)',
            }}
          >
            <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>一括登録の確認</h2>
            <div style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
              <div><b>対象件数:</b> {stats.approved}件</div>
              <div><b>合計金額:</b> {fmt(
                entries.filter(e => e.status === 'approved').reduce((s, e) => s + e.grandTotal, 0)
              )}</div>
              <div style={{ marginTop: 12, padding: 12, background: '#fff4e5', color: '#8b5000', borderRadius: 4, fontSize: 13 }}>
                <MdWarning style={{ verticalAlign: 'middle' }} /> Claude Codeに一括登録指示を送信します。<br />
                ・freeeで既存deal検索による重複チェック実行<br />
                ・各証憑をファイルボックスにアップロード→deal作成<br />
                ・進捗はActivityパネルに実況されます
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button
                appearance="secondary"
                small
                onClick={() => setBatchRegisterOpen(false)}
                disabled={batchRegistering}
              >
                キャンセル
              </Button>
              <Button
                appearance="primary"
                small
                disabled={batchRegistering}
                onClick={async () => {
                  setBatchRegistering(true);
                  try {
                    const approvedEntries = entries.filter(
                      (e) => e.status === 'approved' && !e.alreadyRegistered,
                    );
                    const skippedRegistered = entries.filter(
                      (e) => e.status === 'approved' && e.alreadyRegistered,
                    );

                    if (approvedEntries.length === 0) {
                      setBatchResultToast(
                        skippedRegistered.length > 0
                          ? `全${skippedRegistered.length}件は既に登録済み。処理対象なし。`
                          : '承認済みの未登録エントリがありません。',
                      );
                      setBatchRegisterOpen(false);
                      setBatchRegistering(false);
                      setTimeout(() => setBatchResultToast(null), 6000);
                      return;
                    }

                    // 2026-05-22: 一括登録は per-RID register-via-worker loop に変更。
                    // register_approved_batch instruction は channel-poller が noop 完了させるだけで
                    // freee POST chain が走らない (= 旧 flow) ため。
                    // R2 [[feedback_verification_cleanup]] 準拠: UI click 起点、 worker endpoint 経由。
                    // session 取得失敗 (= audit blocking) でも worker 側で 直近 valid session を
                    // 自動 fallback するため null 続行 OK。
                    const sid = (await ensureSessionLocal().catch(() => null)) || '';
                    setBatchRegisterOpen(false);
                    setActivityOpen(true);

                    let ok = 0;
                    let fail = 0;
                    const failedRefs: string[] = [];
                    for (let i = 0; i < approvedEntries.length; i += 1) {
                      const e = approvedEntries[i];
                      const ref = entryRefNumber(e);
                      setBatchResultToast(`一括登録中: ${i + 1}/${approvedEntries.length} (${ref})`);
                      try {
                        await registerProposalViaWorker(ref, sid);
                        ok += 1;
                      } catch (err: any) {
                        console.error('[bulk-register] failed', ref, err);
                        fail += 1;
                        failedRefs.push(ref);
                      }
                    }
                    const skippedMsg = skippedRegistered.length > 0 ? `（既登録${skippedRegistered.length}件スキップ）` : '';
                    const failMsg = fail > 0 ? ` / 失敗${fail}件 (${failedRefs.slice(0, 3).join(', ')}${fail > 3 ? '...' : ''})` : '';
                    setBatchResultToast(`一括登録完了: 成功${ok}件${failMsg}${skippedMsg}`);
                    setTimeout(() => setBatchResultToast(null), 12000);
                  } catch (err: any) {
                    setBatchResultToast(`送信失敗: ${err.message}`);
                    setTimeout(() => setBatchResultToast(null), 6000);
                  } finally {
                    setBatchRegistering(false);
                  }
                }}
              >
                {batchRegistering ? '送信中...' : '一括登録を実行'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {batchResultToast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 2000,
          background: '#252525', color: '#fff', padding: '12px 16px', borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,.3)', fontSize: 13, maxWidth: 400,
        }}>
          {batchResultToast}
        </div>
      )}

      {/* 一括承認フルスクリーン進捗オーバーレイ — 承認開始〜バッチenroll完了までグレーアウト(操作不可) */}
      {bulkProgress && (
        <div
          role="dialog"
          aria-modal="true"
          aria-busy={bulkProgress.phase !== 'error'}
          style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            background: 'rgba(29,42,58,0.62)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', width: 560, maxWidth: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,.35)' }}>
            {bulkProgress.phase !== 'error' ? (
              <>
                <h2 style={{ margin: '0 0 6px', fontSize: 18 }}>
                  {bulkProgress.phase === 'approving' ? '一括承認を実行中' : 'バッチ完全性台帳へ登録中'}
                </h2>
                <div style={{ fontSize: 12, color: '#6b6560', marginBottom: 16 }}>
                  処理が完了するまでこのままお待ちください — 完了すると自動でバッチ完全性台帳(#batches)へ移動し、登録・照合・証憑添付の進捗を追跡できます
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <InlineSpinner isLoading />
                  <span style={{ fontFamily: '"SFMono-Regular",Consolas,Menlo,monospace', fontSize: 22, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                    {bulkProgress.phase === 'approving'
                      ? `承認中 ${bulkProgress.done}/${bulkProgress.total}`
                      : `台帳登録中 ${bulkProgress.approvedRefs.length}件`}
                  </span>
                </div>
                <ProgressBar
                  value={bulkProgress.phase === 'approving' ? bulkProgress.done : bulkProgress.total}
                  maxValue={bulkProgress.total}
                  width="full"
                  ariaLabel="一括承認進捗"
                />
                <div style={{ marginTop: 12, fontSize: 12, color: '#6b6560' }}>
                  手順: 1/2 承認 → 2/2 バッチ台帳enroll (freeeへの登録・照合・証憑添付は worker が実行し #batches で全件追跡)
                </div>
                {bulkProgress.failed > 0 && (
                  <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: '#fdecea', border: '1px solid #e6b0ac', color: '#b3261e', fontSize: 12, fontWeight: 700 }}>
                    承認失敗 {bulkProgress.failed}件 — 該当行は元のステータスに戻します(後で再選択して承認できます)
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 style={{ margin: '0 0 12px', fontSize: 18, color: '#b3261e', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MdWarning size={22} /> 一括承認の処理でエラーが発生しました
                </h2>
                <div style={{ padding: '12px 14px', borderRadius: 6, background: '#fdecea', border: '1px solid #e6b0ac', color: '#b3261e', fontSize: 14, fontWeight: 700, lineHeight: 1.6, marginBottom: 12, wordBreak: 'break-word' }}>
                  {bulkProgress.errorText}
                </div>
                {bulkProgress.approvedRefs.length > 0 && (
                  <div style={{ fontSize: 12, color: '#6b6560', lineHeight: 1.7, marginBottom: 16 }}>
                    承認 {bulkProgress.approvedRefs.length}件 自体は完了しています。[再試行] でその{bulkProgress.approvedRefs.length}件をバッチ完全性台帳へ登録し直します。閉じた場合も #batches から手動でenrollできます。
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button appearance="secondary" small onClick={() => setBulkProgress(null)}>閉じる</Button>
                  {bulkProgress.approvedRefs.length > 0 && (
                    <Button appearance="primary" small onClick={retryEnroll}>再試行(台帳へ登録)</Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Floating toasts */}
      {toasts.map((t) => (
        <FloatingMessageBlock
          key={t.id}
          success={t.kind === 'success'}
          error={t.kind === 'error'}
          notice={t.kind === 'notice'}
          info={t.kind === 'info'}
          onClose={() =>
            setToasts((prev) => prev.filter((x) => x.id !== t.id))
          }
        >
          {t.text}
        </FloatingMessageBlock>
      ))}
    </div>
  );
};
